import {observer} from 'mobx-react';
import '../geography-filter.scss';
import {ReactNode, useContext, useEffect, useMemo} from 'react';
import {NetworkContext} from '../../../lib/context';
import {FilterProps} from '../filter-types';
import {runInAction} from 'mobx';
import {ReportsPageTab, ViewType} from '../../../pages/reports-page/useReportsPageController';
import {StandardType} from '../../../lib/types';
import FilterContainer from '../filter-container';

interface IReportsNOFiltersProps {
    selectedViewType: ViewType;
    activeTab: ReportsPageTab;
}

const ReportsNOFilters = observer((props: IReportsNOFiltersProps) => {
    
    const {selectedViewType, activeTab} = props;

    const noStore = useContext(NetworkContext);
    useEffect(() => {
        runInAction(() => {
            noStore.appliedGeographyFilter.state = '';
            noStore.appliedGeographyFilter.county = '';
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (noStore.runId && noStore.availableGeographyFilters.length === 0 && !noStore.loadingGeographyData) {
            noStore.loadGeographyData().catch((err) => {
                noStore.handleApiError(err, 'Failed to Load Geography Filters', () => noStore.loadGeographyData());
            });
        }
    }, [noStore.runId]);

    useEffect(() => {
        const controller = new AbortController();
        const loadSpecialtiesData = async () => {
            // Clear specialty filter when switching view types (specialties are not shared across types)
            noStore.setSpecialtyFilter('');
            await noStore.loadSpecialtiesData(controller, selectedViewType.name, false);
        };
        loadSpecialtiesData().catch((err) => {
            if (err.name === 'AbortError') {
                return;
            }
            noStore.handleApiError(err, 'Failed to Load Specialty Data');
        });
        return () => controller.abort();
    }, [
        noStore.runId,
        noStore.readyToLoadData(),
        selectedViewType.name
    ]);

    const adequacyStandardFilter: FilterProps = useMemo(() => {
        const data = noStore.getGeoFilterUniqueValuesByKey('GEOGRAPHY', 'standard_type');
        const onChange = (e: string) => {
            noStore.setGeographyFilter('standard_type', e);
        };
        const children: [(item: string) => string, (item: string) => ReactNode] = [
            (item: string) => item,
            (item: string) => item,
        ];
        return {
            label: 'Adequacy Standard',
            data,
            value: noStore.appliedGeographyFilter.standard_type,
            required: true,
            onChange,
            children,
            spinner: noStore.loadingGeographyData,
            ['data-testid']: 'roster-adequacy-standard-dropdown',
        };
    }, [noStore.runId, noStore.loadingGeographyData, noStore.appliedGeographyFilter.standard_type]);

    const standardVersionFilter: FilterProps = useMemo(() => {
        const geoFilterArgs = {
            standard_type: noStore.appliedGeographyFilter.standard_type,
        };
        const data = noStore.getGeoFilterUniqueValuesByKey(
            'GEOGRAPHY',
            'standard_version',
            geoFilterArgs,
            '',
            'DESC',
        );
        const onChange = (e: string) => {
            noStore.setGeographyFilter('standard_version', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item,
        ];
        return {
            label: 'Standard Version',
            data,
            value: noStore.appliedGeographyFilter.standard_version,
            required: true,
            onChange,
            children,
            spinner: noStore.loadingGeographyData,
            ['data-testid']: 'standard-version-dropdown',
        };
    }, [
        noStore.runId,
        noStore.loadingGeographyData,
        noStore.appliedGeographyFilter.standard_type,
        noStore.appliedGeographyFilter.standard_version
    ]);

    const patientRosterFilter: FilterProps = useMemo(() => {
        const geoFilterArgs = {
            standard_type: noStore.appliedGeographyFilter.standard_type,
            standard_version: noStore.appliedGeographyFilter.standard_version,
        };
        const data = noStore.getGeoFilterUniqueValuesByKey(
            'GEOGRAPHY',
            'patient_roster_name',
            geoFilterArgs,
        );
        const onChange = (e: string) => {
            noStore.setGeographyFilter('patient_roster_name', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item,
        ];
        return {
            label: 'Patient Roster',
            data,
            value: noStore.appliedGeographyFilter.patient_roster_name,
            required: true,
            onChange,
            children,
            spinner: noStore.loadingGeographyData,
            ['data-testid']: 'patient-roster-dropdown',
        };
    }, [
        noStore.runId,
        noStore.loadingGeographyData,
        noStore.appliedGeographyFilter.standard_type,
        noStore.appliedGeographyFilter.standard_version,
        noStore.appliedGeographyFilter.patient_roster_name
    ]);
    
    const stateData = useMemo(() => {
        const geoFilterArgs = {
            patient_roster_name: noStore.appliedGeographyFilter.patient_roster_name,
            standard_type: noStore.appliedGeographyFilter.standard_type,
            standard_version: noStore.appliedGeographyFilter.standard_version,
        };
        return noStore.getGeoFilterUniqueValuesByKey(
            'GEOGRAPHY',
            'state_additional_info.state',
            activeTab === ReportsPageTab.NetworkAdequacy ? geoFilterArgs : undefined
        );
    }, [
        activeTab,
        noStore.runId,
        noStore.loadingGeographyData,
        noStore.appliedGeographyFilter.standard_type,
        noStore.appliedGeographyFilter.standard_version,
        noStore.appliedGeographyFilter.patient_roster_name
    ]);

    const stateFilter: FilterProps = useMemo(() => {
        const onChange = (value: string) => {
            noStore.setGeographyFilter('state', value);
        };
        const children: [(item: string) => string, (item: string) => ReactNode] = [
            (item: string) => item,
            (item: string) => item,
        ];
        return {
            label: 'State',
            data: stateData,
            value: noStore.appliedGeographyFilter.state,
            onChange,
            children,
            spinner: noStore.loadingGeographyData,
            enableMultiSelect: true,
            itemToString: (item: any) => item,
            disabled: adequacyStandardFilter.value === StandardType.QHP,
            ['data-testid']: 'roster-state-dropdown',
        };
    }, [
        stateData,
        noStore.appliedGeographyFilter.state,
        noStore.loadingGeographyData,
        adequacyStandardFilter.value,
    ]);
    
    const countyData = useMemo(() => {
        const geoFilterArgs = {
            'state_additional_info.state': noStore.appliedGeographyFilter.state,
            standard_type: noStore.appliedGeographyFilter.standard_type,
            ...(activeTab === ReportsPageTab.NetworkAdequacy ? {
                patient_roster_name: noStore.appliedGeographyFilter.patient_roster_name,
                standard_version: noStore.appliedGeographyFilter.standard_version,
            } : {})
        };
        return noStore.getGeoFilterUniqueValuesByKey('GEOGRAPHY',
            ['county_additional_info.county_name', 'ssacd'],
            geoFilterArgs
        );
    }, [
        noStore.runId,
        noStore.loadingGeographyData,
        noStore.appliedGeographyFilter.state,
        noStore.appliedGeographyFilter.standard_type,
        noStore.appliedGeographyFilter.standard_version,
        noStore.appliedGeographyFilter.patient_roster_name,
        activeTab
    ]);

    const countyFilter: FilterProps = useMemo(() => {
        const onChange = (value: string) => {
            noStore.setGeographyFilter('county', value);
        };
        const children: [(item: string) => string, (item: string) => ReactNode] = [
            (item: string) => item,
            (item: string) => item
        ];
        const disabled = adequacyStandardFilter.value === StandardType.QHP || !noStore.appliedGeographyFilter.state;
        return {
            label: 'County',
            data: countyData,
            value: noStore.appliedGeographyFilter.county,
            onChange,
            children,
            spinner: noStore.loadingGeographyData,
            disabled,
            enableMultiSelect: true,
            maxSelections: 10,
            ['data-testid']: 'roster-county-dropdown'
        };
    }, [
        countyData,
        noStore.appliedGeographyFilter.county,
        noStore.appliedGeographyFilter.state,
        noStore.loadingGeographyData,
        adequacyStandardFilter.value,
    ]);

    const specialtySourceAndData = useMemo(() => {
        const source =
            noStore.defaultComplianceScores.length > 0
                ? noStore.defaultComplianceScores
                : noStore.complianceScores;
        return {source, data: source.map((score) => score.specialtyCode)};
    }, [
        noStore.runId,
        noStore.complianceScores,
        noStore.defaultComplianceScores,
        noStore.appliedGeographyFilter.standard_type,
        noStore.loadingSpecialtyData,
    ]);

    const specialtyFilter: FilterProps = useMemo(() => {
        const {source, data} = specialtySourceAndData;

        const getSpecialtyName = (code: string): string => {
            const specialty = source.find(s => s.specialtyCode === code);
            return specialty ? specialty.name : code;
        };

        const onChange = (value: string) => {
            noStore.setSpecialtyFilter(value);
        };

        const children: [(item: any) => string, (item: any) => ReactNode] = [
            (item: any) => item,
            getSpecialtyName,
        ];

        const disabled = !noStore.appliedGeographyFilter.standard_type;
        return {
            label: 'Specialty',
            data,
            value: noStore.specialtyFilter,
            onChange,
            children,
            spinner: noStore.loadingSpecialtyData,
            disabled,
            enableMultiSelect: true,
            maxSelections: noStore.maxSpecialtySelection,
            itemToString: getSpecialtyName,
            ['data-testid']: 'roster-specialty-dropdown',
        };
    }, [
        specialtySourceAndData,
        noStore.appliedGeographyFilter.standard_type,
        noStore.loadingSpecialtyData,
        noStore.specialtyFilter,
        noStore.maxSpecialtySelection,
    ]);

    useEffect(() => {
        if (!noStore.appliedGeographyFilter.standard_type) {
            noStore.setSpecialtyFilter('');
        }
    }, [noStore.appliedGeographyFilter.standard_type]);

    const filterConfiguration: Array<FilterProps> = useMemo(() => {
        if (activeTab === ReportsPageTab.Roster) {
            return [
                adequacyStandardFilter,
                stateFilter,
                countyFilter,
                specialtyFilter
            ];
        } else if (activeTab === ReportsPageTab.NetworkAdequacy) {
            return [
                adequacyStandardFilter,
                standardVersionFilter,
                patientRosterFilter,
                stateFilter,
                countyFilter,
                specialtyFilter
            ];
        } else {
            return [];
        }
    }, [
        activeTab,
        adequacyStandardFilter,
        standardVersionFilter,
        patientRosterFilter,
        stateFilter,
        countyFilter,
        specialtyFilter
    ]);

    return (
        <FilterContainer
            filterConfiguration={filterConfiguration}
            horizontal={true}
        />
    );
});

export default ReportsNOFilters;